import FileUploader from 'components/UploaderAndDownloader/FileUploader';
import React, { useCallback, useContext, useRef } from 'react';
import DownloadBtn from './DownloadButton';
import DeleteButton from './DeleteButton';
import { createAppError } from 'utils/helpers';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { AppContext, IAppContext } from 'appState/appContext';
import { uploadRestaurantBankSettings } from 'backend/restaurants';
import { postMediaFile } from 'backend/additionalInformation';
import style from './FileBlock.module.scss';

interface IFileBlock {
	bankSettingsFilePath: string | null;
	logoFilePath: string | null;
	settingsId: string;
	setBankSettingFile: (settingsId: string, value: string | null) => void;
	setLogoFile: (settingsId: string, value: string | null) => void;
	deleteBankSettingsFIle: (value: string) => void;
	onImageFileSizeExceeded: () => void;
}

const FileBlock: React.FC<IFileBlock> = ({
	settingsId,
	setBankSettingFile,
	deleteBankSettingsFIle,
	bankSettingsFilePath,
	logoFilePath,
	setLogoFile,
	onImageFileSizeExceeded,
}) => {
	const { addAppError, showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const {
		isShowValidationError: isShowBankSettingsValidationError,
		validationErrorMessage: bankSettingsValidationErrorMessage,
	} = useAdditionalValidation(
		bankSettingsFilePath,
		'You should upload bank settings',
		`bankSettingsFile-${settingsId}`,
		settingsId,
	);
	const { isShowValidationError: isShowLogoValidationError, validationErrorMessage: logoValidationErrorMessage } =
		useAdditionalValidation(logoFilePath, 'You should upload logo', `logoFile-${settingsId}`, settingsId);

	const initialPaymentTemplateFilePath = useRef<string | null>(bankSettingsFilePath);
	const isShowDownloadButton = initialPaymentTemplateFilePath.current !== null;

	const handleSettingsUpload = useCallback(
		async (file: null | File, callback: (value: string) => void) => {
			try {
				if (file) {
					const formData = new FormData();
					formData.append('file', file, file.name);
					showLoader();
					const { data } = await uploadRestaurantBankSettings(formData);
					setBankSettingFile(settingsId, data.fileId);
					callback(file.name);
				}
			} catch (err) {
				const error = err as Error;
				const appError = createAppError(error);
				addAppError(appError);
			} finally {
				hideLoader();
			}
		},
		[settingsId, setBankSettingFile],
	);

	const handleLogoUpload = useCallback(
		async (file: File, callback: (value: string) => void) => {
			const maxFileSizeMB = 1;
			if (file.size > 1000 * 1000 * maxFileSizeMB) {
				onImageFileSizeExceeded();
				return;
			}

			showLoader();
			const formData = new FormData();
			formData.append('file', file, file.name);
			try {
				const { data } = await postMediaFile({ data: formData });
				setLogoFile(settingsId, data.fileId);
				callback(file.name);
			} catch (err) {
				const error = err as Error;
				const appError = createAppError(error);
				addAppError(appError);
			} finally {
				hideLoader();
			}
		},
		[settingsId, setLogoFile],
	);

	return (
		<div className={style.buttons}>
			<div className={style.buttonBlock}>
				<FileUploader
					fileType="json"
					onFileLoad={handleSettingsUpload}
					id={`bankSettingsFile-${settingsId}`}
					label="uploadSettings"
					requiredValidationMessage={
						isShowBankSettingsValidationError ? bankSettingsValidationErrorMessage : undefined
					}
					width={245}
					messagePosition="bottom"
					defaultFileName={bankSettingsFilePath ? 'settings.json' : undefined}
					filePath={bankSettingsFilePath}
				/>
				<FileUploader
					fileType="png"
					onFileLoad={handleLogoUpload}
					id={`logoFile-${settingsId}`}
					label="uploadLogo"
					requiredValidationMessage={isShowLogoValidationError ? logoValidationErrorMessage : undefined}
					width={245}
					messagePosition="bottom"
					defaultFileName={logoFilePath ? 'logo.png' : undefined}
				/>
			</div>
			<div className={style.buttonBlock}>
				<DeleteButton
					idForDeletion={settingsId}
					filePath={bankSettingsFilePath}
					deleteBankSettingsFIle={deleteBankSettingsFIle}
				/>
				{isShowDownloadButton && <DownloadBtn filePath={bankSettingsFilePath} />}
			</div>
		</div>
	);
};

export default React.memo(FileBlock);
